import React, { useMemo } from "react";
import { Table } from "antd";
import ProfileAvatar from "../common/ProfileAvatar";
import styled from "styled-components";
import { getProfileIconLink } from "../../../utils/profile";
import { appNextUrl, constructUrlWithParams, decodeString } from 'findem-helpers';
import { useUrlParams } from "../../../utils/use-url-params";

type ShortlistedProfilesProps = {
  data: any;
  forceLoadImages?: boolean;
}

const DivAsLink = styled.div`
  color: #1890ff;
  cursor: pointer;
`;

const ShortlistedProfiles = (props: ShortlistedProfilesProps) => {
  const { data, forceLoadImages = false } = props;
  const { icpid, joid, accountId } = useUrlParams();

  const onLinkClick = (e: any) => {
    e.preventDefault();
    window.open(e.currentTarget.getAttribute('data-href'), '_blank');
  }

  const buildProfileUrl = (pubId: string) => {
    const params: { icpid?: string, joid?: string, aId?: string } = {};
    if (icpid) params.icpid = icpid;
    if (joid) params.joid = joid;
    if (accountId) params.aId = accountId;
    return constructUrlWithParams(`${appNextUrl}/profiles/${pubId}`, params);
  }

  // Optimized: avoid sorting full arrays; select current roles directly,
  // and fall back to single most-recent via a linear scan
  const currentExps = (currentProfile: any) => {
    const currents = [] as any[];
    for (const we of currentProfile.workexp || []) {
      const isCurrent = (we.duration && we.duration.to_present === true) ||
        (we.projects && we.projects.length > 0 && we.projects[0].duration && we.projects[0].duration.to_present === true);
      if (isCurrent) currents.push(we);
    }
    if (currents.length > 0) return currents;
    // find most recent without sorting
    let mostRecent = null as any;
    let mostRecentTime = -Infinity;
    for (const we of currentProfile.workexp || []) {
      const start = we?.duration?.start_date;
      if (!start) continue;
      const t = new Date(start.includes('T') ? start.split('T')[0] : start).getTime();
      if (t > mostRecentTime) {
        mostRecentTime = t;
        mostRecent = we;
      }
    }
    return mostRecent ? [mostRecent] : [];
  }

  const getPubId = (currentProfile) => {
    const { ext_refs } = currentProfile;
    const _linkedRef = ext_refs.find((ref) => ref.source === "Findem");
    return _linkedRef?.source_ref || '';
  }

    const columns = useMemo(() => ([
      {
        title: 'Picture',
        dataIndex: 'image',
        key: 'image',
        width: 50,
        render: (text, record) => (
          <ProfileAvatar
            imageUrl={record.image}
            fullName={record.name}
            imageHeight={50}
            imageWidth={50}
            fontSize={18}
            forceLoad={forceLoadImages}
          />
        )
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 150,
        render: (text, record) => (
          <DivAsLink onClick={onLinkClick} data-href={buildProfileUrl(record.pubId)}>{decodeString(record.name || '')}</DivAsLink>
        )
      },
      {
        title: 'Position',
        dataIndex: 'role',
        key: 'role',
        width: 350,
        render: (text, record) => (
          (record.role || []).map((role, index) => <p style={{ wordWrap: 'break-word', wordBreak: 'break-word' }} key={index}>{decodeString(role || '')}</p>)
        )
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        key: 'notes',
        width: 250,
        render: (text, record) => (
          (record.notes || []).map((note, index) => <p style={{ wordWrap: 'break-word', wordBreak: 'break-word' }} key={index}>{note}</p>)
        )
      }
    ]), [forceLoadImages, onLinkClick]);


  const columnData = useMemo(() => {
    const _columnData = [];
    Object.keys(data).forEach((key) => {
      const currentProfile = data[key];
      const currentExperiences = currentExps(currentProfile);
      _columnData.push({
        key,
        name: currentProfile.candidate.full_name,
        role: currentExperiences.map(exp => `${exp.projects[0]?.role_and_group?.title} at ${exp.company?.name}`) || [],
        image: getProfileIconLink(currentProfile) || '',
        pubId: getPubId(currentProfile),
        notes: currentProfile.notes,
      });
    });
    return _columnData;
  }, [data]);

  return (<>
    <Table
      dataSource={columnData}
      columns={columns}
      pagination={false}
      rowKey={(row) => row.key}
    />
  </>)
}

export default React.memo(ShortlistedProfiles);
